// ✅ src/screens/HomeScreen.js (FULL)
// ✅ NOW your "♻️ Refresh" REALLY brings MORE profiles (pagination)
// ✅ Filters REALLY work:
//    - my lookingFor => matches target.gender (unless lookingFor = everyone)
//    - my prefAgeMin/prefAgeMax => filters by age range
// ✅ Still excludes:
//    - myself
//    - already swiped users
// ✅ Uses orderBy(lastActiveAt desc) + startAfter(cursor) for next batches
// ✅ Auto-loads more when you’re near the end of the deck
// ✅ NEW: "IT'S A MATCH!" animated popup + "Open Chat" button (navigates to chat)
//
// IMPORTANT:
// - Make sure your datingProfiles have lastActiveAt saved (you already set it on save).
// - If Firestore asks for an index, create it (it will show a link in console).
// - CHAT ROUTE: by default this navigates to `/chat/${matchId}`.
//   If your ChatScreen uses a different route, change `CHAT_ROUTE_PREFIX` below.

import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { onAuthStateChanged } from "firebase/auth";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  runTransaction,
  serverTimestamp,
  startAfter,
} from "firebase/firestore";
import { auth, db } from "../lib/firebase";

/* ---------------- helpers ---------------- */
function safeStr(v) {
  return String(v ?? "").trim();
}
function num(v, fallback = null) {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
}
function msFromTs(ts) {
  try {
    if (!ts) return null;
    if (typeof ts === "number") return ts;
    if (ts?.toMillis) return ts.toMillis();
    if (ts?.toDate) return ts.toDate().getTime();
    return null;
  } catch {
    return null;
  }
}
function calcAgeFromBirthDate(birthDateTs) {
  const ms = msFromTs(birthDateTs);
  if (!ms) return null;
  const dob = new Date(ms);
  const now = new Date();
  let age = now.getFullYear() - dob.getFullYear();
  const m = now.getMonth() - dob.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < dob.getDate())) age -= 1;
  if (age < 0 || age > 120) return null;
  return age;
}
function initials(name) {
  const t = safeStr(name);
  if (!t) return "?";
  const parts = t.split(" ").filter(Boolean);
  const a = parts[0]?.[0] ?? "?";
  const b = parts[1]?.[0] ?? "";
  return (a + b).toUpperCase();
}
function makeChatId(a, b) {
  const x = safeStr(a);
  const y = safeStr(b);
  if (!x || !y) return "";
  return [x, y].sort().join("__");
}
function makeLikeId(fromUid, toUid) {
  const a = safeStr(fromUid);
  const b = safeStr(toUid);
  return a && b ? `${a}__${b}` : "";
}

/* ---------------- UI bits ---------------- */
function BigProfileCard({ person, onOpenProfile, swipeOut }) {
  const name = safeStr(person?.displayName) || "Unknown";
  const avatar = safeStr(person?.avatar) || safeStr(person?.photos?.[0]);
  const city = safeStr(person?.city) || "Nearby";
  const age = Number.isFinite(Number(person?.age)) ? Number(person.age) : null;

  const animStyle =
    swipeOut === "right"
      ? S.cardSwipeRight
      : swipeOut === "left"
      ? S.cardSwipeLeft
      : null;

  return (
    <button
      type="button"
      style={S.bigCardBtn}
      onClick={onOpenProfile}
      aria-label={`Open ${name}'s profile`}
      title="View profile"
    >
      <div style={{ ...S.bigCard, ...(animStyle || null) }}>
        <div style={S.bigMedia}>
          {avatar ? (
            <img src={avatar} alt={name} style={S.bigImg} />
          ) : (
            <div style={S.bigFallback}>{initials(name)}</div>
          )}

          <div style={S.bigOverlay}>
            <div style={S.bigName}>
              {name}
              {age !== null ? <span style={{ opacity: 0.85 }}> · {age}</span> : null}
            </div>

            <div style={S.bigMeta}>
              <span style={S.pill}>📍 {city}</span>
              {person?.verified ? (
                <span style={S.pill}>✅ Verified</span>
              ) : (
                <span style={S.pill}>✨ New</span>
              )}
              {safeStr(person?.relationshipGoal) ? (
                <span style={S.pill}>🔥 {safeStr(person.relationshipGoal)}</span>
              ) : null}
            </div>

            {safeStr(person?.bio) ? (
              <div style={S.bigBio}>{safeStr(person.bio)}</div>
            ) : (
              <div style={S.bigTapHint}>Tap to view full profile</div>
            )}
          </div>
        </div>
      </div>
    </button>
  );
}

function MatchModal({ open, person, onClose, onChat }) {
  const name = safeStr(person?.displayName) || "Someone";
  const avatar = safeStr(person?.avatar) || safeStr(person?.photos?.[0]);

  return (
    <div
      style={{ ...S.matchOverlay, ...(open ? S.matchOverlayOpen : S.matchOverlayClosed) }}
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <div
        style={{ ...S.matchCard, ...(open ? S.matchCardOpen : S.matchCardClosed) }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={S.matchHeader}>
          <div style={S.matchTitle}>It’s a Match! 💖</div>
          <button type="button" style={S.matchX} onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>

        <div style={S.matchBody}>
          <div style={S.matchBurst} />
          <div style={S.matchRow}>
            <div style={S.matchAvatar}>
              {avatar ? (
                <img src={avatar} alt={name} style={S.matchAvatarImg} />
              ) : (
                <div style={S.matchAvatarFallback}>{initials(name)}</div>
              )}
            </div>

            <div style={S.matchText}>
              <div style={S.matchLine1}>
                You and <span style={{ fontWeight: 1000 }}>{name}</span> liked each other.
              </div>
              <div style={S.matchLine2}>Say hi and start chatting now.</div>
            </div>
          </div>
        </div>

        <div style={S.matchActions}>
          <button type="button" style={S.matchBtnGhost} onClick={onClose}>
            Keep swiping
          </button>
          <button type="button" style={S.matchBtn} onClick={onChat}>
            💬 Open chat
          </button>
        </div>
      </div>
    </div>
  );
}

/* ---------------- main ---------------- */
export default function HomeScreen() {
  const nav = useNavigate();

  const CHAT_ROUTE_PREFIX = "/chat/"; // ✅ change this if your route is different

  const PAGE_SIZE = 200; // fetch batch size (not what you show; you filter client-side)
  const AUTO_LOAD_WHEN_LEFT = 3; // if 3 cards left, auto load more

  const [myUid, setMyUid] = useState("");
  const [deck, setDeck] = useState([]);
  const [index, setIndex] = useState(0);
  const [acting, setActing] = useState(false);
  const [swipeOut, setSwipeOut] = useState(""); // "" | "left" | "right"

  // pagination states
  const [cursor, setCursor] = useState(null); // last doc snapshot
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  // ✅ match popup states
  const [matchOpen, setMatchOpen] = useState(false);
  const [matchPerson, setMatchPerson] = useState(null);
  const [matchId, setMatchId] = useState(""); // the chat/match doc id to open

  const current = deck[index] || null;

  const openPublicProfile = (uid) => {
    const id = safeStr(uid);
    if (!id) return;
    nav(`/public-profile/${id}`);
  };

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (u) => {
      if (!u) {
        nav("/login", { replace: true });
        return;
      }
      setMyUid(u.uid);
      await resetAndLoad(u.uid);
    });
    return () => unsub();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nav]);

  // ✅ reset feed and load first batch
  async function resetAndLoad(uid) {
    setDeck([]);
    setIndex(0);
    setCursor(null);
    setHasMore(true);
    await loadMore(uid, { reset: true });
  }

  // ✅ REAL pagination: loads next batch and APPENDS new profiles
  async function loadMore(uid, { reset = false } = {}) {
    const userId = safeStr(uid);
    if (!userId) return;
    if (loadingMore) return;
    if (!hasMore && !reset) return;

    setLoadingMore(true);

    try {
      // read my preferences
      const myRef = doc(db, "datingProfiles", userId);
      const mySnap = await getDoc(myRef);
      const me = mySnap.exists() ? mySnap.data() : {};

      const myLookingFor = safeStr(me?.lookingFor).toLowerCase() || "everyone"; // male|female|everyone
      const prefMin = num(me?.prefAgeMin, 18) ?? 18;
      const prefMax = num(me?.prefAgeMax, null); // null means "no max" (but you require it in creation)

      // load swiped ids
      const swipedIds = new Set();
      try {
        const swipeSnap = await getDocs(
          query(collection(db, "datingProfiles", userId, "swipes"), limit(800))
        );
        swipeSnap.forEach((d) => swipedIds.add(d.id));
      } catch {}

      // build paginated query
      const base = [
        collection(db, "datingProfiles"),
        orderBy("lastActiveAt", "desc"),
        limit(PAGE_SIZE),
      ];

      const qy = reset || !cursor ? query(...base) : query(...base, startAfter(cursor));
      const snap = await getDocs(qy);

      // update pagination flags
      const docs = snap.docs || [];
      const last = docs[docs.length - 1] || null;
      setCursor(last);
      setHasMore(docs.length === PAGE_SIZE); // if smaller, likely exhausted

      // filter results
      const incoming = [];
      for (const d of docs) {
        if (d.id === userId) continue;
        if (swipedIds.has(d.id)) continue;

        const data = d.data() || {};
        const targetGender = safeStr(data.gender).toLowerCase();

        // gender filter
        if (myLookingFor !== "everyone") {
          if (!targetGender) continue; // if target missing gender, skip
          if (targetGender !== myLookingFor) continue;
        }

        // age filter
        const computed = calcAgeFromBirthDate(data?.birthDate);
        const ageVal = Number.isFinite(Number(data?.age)) ? Number(data.age) : computed;
        if (!Number.isFinite(Number(ageVal))) continue;

        if (Number(ageVal) < Number(prefMin)) continue;
        if (prefMax != null && Number(ageVal) > Number(prefMax)) continue;

        incoming.push({
          uid: d.id,
          displayName: safeStr(data.displayName),
          avatar: safeStr(data.avatar),
          photos: Array.isArray(data.photos) ? data.photos : [],
          age: Number(ageVal),
          verified: Boolean(data.verified),
          city: safeStr(data.city),
          bio: safeStr(data.bio || data.about || data.description),
          relationshipGoal: safeStr(data.relationshipGoal),
        });
      }

      setDeck((prev) => {
        // avoid duplicates
        const seen = new Set((prev || []).map((p) => p.uid));
        const merged = [...(prev || [])];
        for (const p of incoming) {
          if (!seen.has(p.uid)) merged.push(p);
        }
        return merged;
      });
    } catch (e) {
      console.error("loadMore error", e);
      // if query fails, don't kill feed; just stop hasMore to avoid endless retry
      setHasMore(false);
    } finally {
      setLoadingMore(false);
    }
  }

  // ✅ UPDATED: returns matchId when matched
  async function writeSwipe({ action, target }) {
    const u = auth.currentUser;
    if (!u || !target?.uid) return { matched: false, matchId: "" };

    const my = u.uid;
    const other = target.uid;

    let matched = false;
    let outMatchId = "";

    await runTransaction(db, async (tx) => {
      const swipeRef = doc(db, "datingProfiles", my, "swipes", other);

      if (action !== "like") {
        tx.set(swipeRef, { action, createdAt: serverTimestamp() }, { merge: true });
        return;
      }

      const likeId = makeLikeId(my, other);
      const reverseLikeId = makeLikeId(other, my);

      const likeRef = doc(db, "likes", likeId);
      const reverseRef = doc(db, "likes", reverseLikeId);

      const reverseSnap = await tx.get(reverseRef);

      tx.set(swipeRef, { action: "like", createdAt: serverTimestamp() }, { merge: true });

      tx.set(
        likeRef,
        { createdAt: serverTimestamp(), fromUid: my, toUid: other, isSuperLike: false },
        { merge: true }
      );

      if (reverseSnap.exists()) {
        matched = true;

        const mId = makeChatId(my, other);
        outMatchId = mId;

        const matchRef = doc(db, "matches", mId);

        tx.set(
          matchRef,
          {
            createdAt: serverTimestamp(),
            isActive: true,
            lastMessageAt: serverTimestamp(),
            lastMessageText: "",
            unreadCountA: 0,
            unreadCountB: 0,
            userA: my,
            userB: other,
          },
          { merge: true }
        );
      }
    });

    return { matched, matchId: outMatchId };
  }

  async function animateThenNext(dir, fn) {
    if (!current || acting) return;
    setActing(true);
    setSwipeOut(dir);

    try {
      await fn?.();
    } catch (e) {
      console.error("swipe action error", e);
    }

    setTimeout(() => {
      setIndex((i) => i + 1);
      setSwipeOut("");
      setActing(false);
    }, 260);
  }

  // ✅ auto-load when close to end
  useEffect(() => {
    if (!myUid) return;
    const left = (deck?.length || 0) - index;
    if (left <= AUTO_LOAD_WHEN_LEFT && hasMore && !loadingMore) {
      loadMore(myUid);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [index, deck.length, myUid, hasMore, loadingMore]);

  async function onPass() {
    await animateThenNext("left", async () => {
      await writeSwipe({ action: "pass", target: current });
    });
  }

  async function onLike() {
    const liked = current;
    let result = { matched: false, matchId: "" };

    await animateThenNext("right", async () => {
      result = await writeSwipe({ action: "like", target: liked });
    });

    // ✅ if matched, show animated modal and allow opening chat
    if (result?.matched && safeStr(result.matchId)) {
      setMatchPerson(liked);
      setMatchId(result.matchId);

      // open modal with animation (ensure render first)
      setMatchOpen(true);
    }
  }

  const closeMatch = () => {
    setMatchOpen(false);
    // keep info a bit (optional). Clear after close animation
    setTimeout(() => {
      setMatchPerson(null);
      setMatchId("");
    }, 240);
  };

  const openChatFromMatch = () => {
    const id = safeStr(matchId);
    if (!id) return;
    closeMatch();
    nav(`${CHAT_ROUTE_PREFIX}${id}`);
  };

  return (
    <div style={S.stage}>
      {/* ✅ MATCH POPUP */}
      <MatchModal
        open={matchOpen}
        person={matchPerson}
        onClose={closeMatch}
        onChat={openChatFromMatch}
      />

      <div style={S.homeHeaderRow}>
        <button
          type="button"
          style={S.secondaryPillBtn}
          onClick={() => nav("/meetups")}
          title="Explore meetups"
        >
          👉 Click here to find Meetups
        </button>

        {/* ✅ Refresh now loads MORE (next page) */}
        <button
          type="button"
          style={S.secondaryPillBtn}
          onClick={() => {
            const u = auth.currentUser;
            if (!u) return;
            // If deck is empty, start fresh; otherwise load next page
            if (!deck?.length) resetAndLoad(u.uid);
            else loadMore(u.uid);
          }}
          title="Load more profiles"
          disabled={loadingMore}
        >
          {loadingMore ? "⏳ Loading..." : "♻️ Refresh"}
        </button>
      </div>

      {!current ? (
        <div style={S.blankState}>
          <div style={S.blankStateText}>
            {loadingMore ? "Loading more profiles..." : "No more profiles to show."}
          </div>
        </div>
      ) : (
        <>
          <BigProfileCard
            person={current}
            swipeOut={swipeOut}
            onOpenProfile={() => openPublicProfile(current.uid)}
          />

          <div style={S.quickRow}>
            <button
              type="button"
              style={S.quickBtn}
              onClick={() => openPublicProfile(current.uid)}
              title="Open profile"
            >
              👀 View profile details
            </button>
          </div>

          <div style={S.actions}>
            <button
              type="button"
              style={{ ...S.actionBtn, ...S.noBtn }}
              onClick={onPass}
              disabled={acting}
              aria-label="Pass"
              title="Pass"
            >
              ❌
            </button>

            <button
              type="button"
              style={{ ...S.actionBtn, ...S.likeBtn }}
              onClick={onLike}
              disabled={acting}
              aria-label="Like"
              title="Like"
            >
              ❤️
            </button>
          </div>

          <div style={S.hint}>
            Tip: Tap the card to view profile • ❤️ Like (swipes right) • ❌ Pass (swipes left)
          </div>
        </>
      )}
    </div>
  );
}

/* ---------------- styles ---------------- */
const S = {
  stage: { display: "grid", gap: 14, justifyItems: "center" },

  homeHeaderRow: {
    width: "min(640px, 100%)",
    display: "flex",
    gap: 10,
    justifyContent: "space-between",
    alignItems: "center",
    flexWrap: "wrap",
  },
  secondaryPillBtn: {
    border: "1px solid rgba(2,6,23,0.10)",
    background: "rgba(255,255,255,0.78)",
    borderRadius: 999,
    padding: "10px 12px",
    cursor: "pointer",
    fontWeight: 1000,
    boxShadow: "0 12px 28px rgba(2,6,23,0.08)",
  },

  bigCardBtn: {
    padding: 0,
    border: "none",
    background: "transparent",
    cursor: "pointer",
    width: "min(640px, 100%)",
  },
  bigCard: {
    width: "min(640px, 100%)",
    borderRadius: 28,
    border: "1px solid rgba(2,6,23,0.07)",
    background: "rgba(255,255,255,0.72)",
    boxShadow: "0 22px 70px rgba(2,6,23,0.10)",
    overflow: "hidden",
    transition: "transform 240ms ease, opacity 240ms ease",
    willChange: "transform, opacity",
  },
  cardSwipeRight: { transform: "translateX(120%) rotate(10deg)", opacity: 0.0 },
  cardSwipeLeft: { transform: "translateX(-120%) rotate(-10deg)", opacity: 0.0 },

  bigMedia: { position: "relative" },
  bigImg: { width: "100%", height: 520, objectFit: "cover", display: "block" },
  bigFallback: {
    width: "100%",
    height: 520,
    display: "grid",
    placeItems: "center",
    background: "rgba(2,6,23,0.85)",
    color: "#fff",
    fontWeight: 1000,
    fontSize: 44,
  },
  bigOverlay: {
    position: "absolute",
    left: 0,
    right: 0,
    bottom: 0,
    padding: 16,
    background:
      "linear-gradient(180deg, transparent 0%, rgba(0,0,0,.50) 35%, rgba(0,0,0,.78) 100%)",
    color: "#fff",
  },
  bigName: { fontWeight: 1000, fontSize: 22, letterSpacing: -0.3 },
  bigMeta: { display: "flex", flexWrap: "wrap", gap: 8, marginTop: 10 },
  bigBio: {
    marginTop: 10,
    fontWeight: 800,
    opacity: 0.9,
    lineHeight: 1.35,
    maxWidth: 560,
  },
  bigTapHint: { marginTop: 10, fontWeight: 900, opacity: 0.9, fontSize: 13 },
  pill: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "6px 10px",
    borderRadius: 999,
    fontWeight: 900,
    fontSize: 12,
    background: "rgba(255,255,255,0.16)",
    border: "1px solid rgba(255,255,255,0.22)",
    whiteSpace: "nowrap",
  },

  quickRow: { width: "min(640px, 100%)", display: "flex", justifyContent: "center" },
  quickBtn: {
    border: "1px solid rgba(2,6,23,0.10)",
    background: "rgba(255,255,255,0.78)",
    borderRadius: 16,
    padding: "10px 12px",
    cursor: "pointer",
    fontWeight: 1000,
    width: "100%",
    maxWidth: 320,
  },

  actions: {
    display: "flex",
    gap: 16,
    alignItems: "center",
    justifyContent: "center",
    marginTop: 2,
  },
  actionBtn: {
    width: 62,
    height: 62,
    borderRadius: 999,
    border: "1px solid rgba(2,6,23,0.10)",
    cursor: "pointer",
    fontSize: 26,
    fontWeight: 1000,
    boxShadow: "0 18px 50px rgba(2,6,23,0.12)",
    background: "rgba(255,255,255,0.78)",
  },
  noBtn: { background: "rgba(255,255,255,0.80)" },
  likeBtn: {
    background: "linear-gradient(135deg, rgba(236,72,153,1), rgba(99,102,241,1))",
    color: "#fff",
    border: "none",
  },
  hint: {
    fontWeight: 900,
    opacity: 0.65,
    fontSize: 13,
    textAlign: "center",
    paddingBottom: 6,
  },

  blankState: {
    borderRadius: 24,
    border: "1px solid rgba(2,6,23,0.07)",
    background: "rgba(255,255,255,0.58)",
    boxShadow: "0 18px 50px rgba(2,6,23,0.06)",
    backdropFilter: "blur(12px)",
    padding: 18,
    minHeight: 180,
    display: "grid",
    placeItems: "center",
    textAlign: "center",
    width: "min(640px, 100%)",
  },
  blankStateText: { fontWeight: 900, opacity: 0.65 },

  /* -------- Match modal styles (animated) -------- */
  matchOverlay: {
    position: "fixed",
    inset: 0,
    zIndex: 9999,
    display: "grid",
    placeItems: "center",
    padding: 16,
    transition: "opacity 220ms ease",
    background: "rgba(2,6,23,0.60)",
  },
  matchOverlayOpen: { opacity: 1, pointerEvents: "auto" },
  matchOverlayClosed: { opacity: 0, pointerEvents: "none" },

  matchCard: {
    width: "min(520px, 100%)",
    borderRadius: 22,
    overflow: "hidden",
    border: "1px solid rgba(255,255,255,0.16)",
    background: "rgba(255,255,255,0.92)",
    boxShadow: "0 28px 90px rgba(2,6,23,0.40)",
    transformOrigin: "center",
    transition: "transform 220ms ease, opacity 220ms ease",
    position: "relative",
  },
  matchCardOpen: { transform: "scale(1)", opacity: 1 },
  matchCardClosed: { transform: "scale(0.92)", opacity: 0 },

  matchHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    padding: "14px 16px",
    background:
      "linear-gradient(90deg, rgba(99,102,241,0.16), rgba(236,72,153,0.14))",
    borderBottom: "1px solid rgba(2,6,23,0.06)",
  },
  matchTitle: { fontWeight: 1100, fontSize: 16, letterSpacing: -0.2, color: "#0f172a" },
  matchX: {
    border: "none",
    background: "transparent",
    cursor: "pointer",
    fontSize: 18,
    fontWeight: 1000,
    color: "#0f172a",
  },

  matchBody: { padding: 16, position: "relative" },
  matchBurst: {
    position: "absolute",
    inset: -60,
    background:
      "radial-gradient(circle at 30% 30%, rgba(236,72,153,0.20), transparent 55%), radial-gradient(circle at 70% 60%, rgba(99,102,241,0.20), transparent 55%)",
    filter: "blur(6px)",
    pointerEvents: "none",
  },
  matchRow: { display: "flex", gap: 14, alignItems: "center", position: "relative" },

  matchAvatar: {
    width: 86,
    height: 86,
    borderRadius: 999,
    overflow: "hidden",
    border: "2px solid rgba(236,72,153,0.25)",
    boxShadow: "0 18px 50px rgba(2,6,23,0.12)",
    background: "rgba(255,255,255,0.9)",
    flex: "0 0 auto",
  },
  matchAvatarImg: { width: "100%", height: "100%", objectFit: "cover" },
  matchAvatarFallback: {
    width: "100%",
    height: "100%",
    display: "grid",
    placeItems: "center",
    fontWeight: 1100,
    fontSize: 28,
    color: "#0f172a",
    background:
      "linear-gradient(135deg, rgba(236,72,153,0.18), rgba(99,102,241,0.18))",
  },

  matchText: { display: "grid", gap: 6, position: "relative" },
  matchLine1: { fontWeight: 950, color: "#0f172a", letterSpacing: -0.2 },
  matchLine2: { fontWeight: 850, opacity: 0.75, color: "#0f172a" },

  matchActions: {
    display: "flex",
    gap: 10,
    justifyContent: "flex-end",
    padding: 16,
    borderTop: "1px solid rgba(2,6,23,0.06)",
  },
  matchBtnGhost: {
    padding: "12px 14px",
    borderRadius: 14,
    border: "1px solid rgba(2,6,23,0.12)",
    background: "rgba(255,255,255,0.9)",
    cursor: "pointer",
    fontWeight: 1000,
  },
  matchBtn: {
    padding: "12px 14px",
    borderRadius: 14,
    border: "none",
    background: "linear-gradient(135deg, rgba(236,72,153,1), rgba(99,102,241,1))",
    color: "#fff",
    cursor: "pointer",
    fontWeight: 1100,
    boxShadow: "0 18px 55px rgba(99,102,241,0.22)",
  },
};